import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getPlpTagFromUrl, isPlpRoute } from '@/features/plp/utils/plpUrl';

interface BannerWidgetBannerVm {
  name: string;
  imageUrl: string;
  desktopImageUrl: string | null;
  landingUrl: string | null;
  bannerType: string;
}

interface BannerWidgetServiceInterface {
  getWidgetBanners(tags?: string): Promise<BannerWidgetBannerVm[]>;
}

export interface UseBannerWidgetBannersOptions {
  service: BannerWidgetServiceInterface;
}

export interface UseBannerWidgetBannersResult {
  banners: BannerWidgetBannerVm[] | null;
  isLoading: boolean;
}

export function useBannerWidgetBanners({
  service,
}: UseBannerWidgetBannersOptions): UseBannerWidgetBannersResult {
  const location = useLocation();

  const [banners, setBanners] = useState<BannerWidgetBannerVm[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const tags = useMemo(() => {
    const pathname = location.pathname || '';

    if (!isPlpRoute(pathname)) return '';

    return getPlpTagFromUrl(pathname);
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true;

    if (!tags) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- start async fetch; state updates happen only from promise callbacks
    setIsLoading(true);
    service
      .getWidgetBanners(tags)
      .then((result) => {
        if (!isMounted) return;
        setBanners(result ?? []);
      })
      .catch((error) => {
        console.error('Failed to load Banner widget banners', error);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [service, tags]);

  return { banners, isLoading };
}

